/**
 * Service for managing non-EPA call and fax history log entries in Firestore.
 *
 * Writes go to /cmm_workflow/{identifier}/nonEPA_logs/{channel}/logs, where the
 * channel doc is `calling_history_logs` or `fax_history_logs` depending on the
 * tab the summary was saved from. Reads also include the legacy flat
 * /cmm_workflow/{identifier}/calling_history_logs collection so summaries saved
 * before the split still appear in the history panes.
 */

import { Timestamp } from "firebase/firestore";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { logError } from "../../../utils/customLogger";

/**
 * One saved non-EPA summary. Fields common to both channels are always written;
 * the channel-specific ones are named for their tab, so a fax entry never
 * carries call wording (`faxed_at` rather than `called_at`) or empty call
 * fields. Read channel-specific values through the helpers in
 * {@link ./callingHistoryFields} so legacy entries keep rendering.
 */
export interface CallingHistoryLog {
  id?: string;
  /**
   * Collection the entry was read from, stamped on by {@link fetchCallingHistoryLogs}
   * so a delete can target the right path. Never written to Firestore.
   */
  collection_path?: string;
  case_id: string;
  comment: string;
  response_status: string;
  case_status: string;
  created_by: string;
  created_at: Timestamp;
  /** Channel the summary was recorded for. Absent on entries saved before the fax tab existed. */
  mode?: "call" | "fax";
  /** Snapshot of the order's second STP status when the summary was saved. */
  second_stp_status?: string;
  /** Free-text next action agreed on the call or fax. */
  next_action?: string;
  /** Follow-up date exactly as the date picker emitted it (YYYY-MM-DD). */
  follow_up_date?: string;

  /** Call entries: date of the call (MM/DD/YYYY). */
  called_date?: string;
  /** Call entries: full display timestamp, e.g. `07/23/2026 12:20 EST`. */
  called_at?: string;
  /** Call entries: Firebase UID of the POC who made the call. */
  calling_poc?: string;
  /** Call entries: reason the order was routed to outreach. */
  calling_reason?: string;
  /** Call entries: whether outreach was required (yes / no / archive). */
  call_required?: string;
  /** Call entries: insurance rep spoken to. */
  rep_name?: string;
  /** Call entries: number dialled. */
  phone_number?: string;

  /** Fax entries: date the fax was sent (MM/DD/YYYY). */
  faxed_date?: string;
  /** Fax entries: full display timestamp, e.g. `07/28/2026 05:06 EST`. */
  faxed_at?: string;
  /** Fax entries: Firebase UID of the POC who sent the fax. */
  poc?: string;
  /** Fax entries: reason the order was routed to outreach. */
  non_epa_reason?: string;
  /** Fax entries: whether outreach was required (yes / no / archive). */
  fax_required?: string;
  /** Fax entries: fax number used. */
  fax_number?: string;
  /** Fax entries: payer or department the fax was sent to. */
  sent_to?: string;
  /** Fax entries: storage path of the attached document. */
  attachment_path?: string;
  /** Fax entries: original file name of the attached document. */
  attachment_name?: string;
  /** Fax entries: download URL for the attached document. */
  attachment_url?: string;
}

/** Doc id under `nonEPA_logs` that owns the entries for each channel tab. */
export const NON_EPA_CHANNEL_DOC_IDS = {
  call: "calling_history_logs",
  fax: "fax_history_logs",
} as const;

/**
 * Resolve the `nonEPA_logs` channel doc id for a log entry's mode.
 *
 * @input mode - Channel the summary was recorded for; absent entries are treated as calls
 * @return The channel doc id to nest the entry under
 */
const resolveChannelDocId = (mode: CallingHistoryLog["mode"]): string =>
  mode === "fax" ? NON_EPA_CHANNEL_DOC_IDS.fax : NON_EPA_CHANNEL_DOC_IDS.call;

/**
 * Save a non-EPA history log entry under the channel matching its mode.
 *
 * @input identifier - The order identifier used as the document key under cmm_workflow
 * @input logData - The history log data to persist; `mode` selects the channel
 * @return The saved log entry with its auto-generated Firestore document ID
 * @throws Error if the identifier is missing or the write fails
 */
export const saveCallingHistoryLog = async (
  identifier: string,
  logData: Omit<CallingHistoryLog, "id" | "created_at">,
): Promise<CallingHistoryLog> => {
  if (!identifier) {
    throw new Error("Identifier is required to save calling history log");
  }

  const collectionPath = FirestoreCollectionReference.nonEpaHistoryLogs(
    identifier,
    resolveChannelDocId(logData.mode),
  );

  const dataWithTimestamp = {
    ...logData,
    created_at: Timestamp.now(),
  };

  const result = await FirestoreService.addDocument(
    collectionPath,
    dataWithTimestamp,
  );

  return result as CallingHistoryLog;
};

/**
 * Convert a log entry's creation time to epoch milliseconds for sorting.
 *
 * @input createdAt - Firestore timestamp, or a date-like value on older entries
 * @return Epoch milliseconds
 */
const toMillis = (createdAt: CallingHistoryLog["created_at"]): number =>
  createdAt instanceof Timestamp
    ? createdAt.toMillis()
    : new Date(createdAt).getTime();

/**
 * Read one history collection, returning an empty list if the path is unreadable.
 *
 * Each entry is stamped with the path it came from, since the three history
 * collections are merged for display and a delete still needs its origin.
 *
 * @input collectionPath - Firestore collection path to read
 * @return Array of history log entries found at the path
 */
const fetchLogsAtPath = async (
  collectionPath: string,
): Promise<CallingHistoryLog[]> => {
  try {
    const logs = await FirestoreService.getAllDocuments<CallingHistoryLog>(
      collectionPath,
    );
    return logs.map((log) => ({ ...log, collection_path: collectionPath }));
  } catch (error) {
    logError(
      error as Error,
      `Error fetching calling history logs at ${collectionPath}`,
    );
    return [];
  }
};

/**
 * Fetch every call and fax history log for an identifier, newest first.
 *
 * Merges both non-EPA channel collections with the legacy flat collection so
 * entries saved before the non-EPA split still show up.
 *
 * @input identifier - The order identifier
 * @return Array of history log entries ordered by creation time, newest first
 */
export const fetchCallingHistoryLogs = async (
  identifier: string,
): Promise<CallingHistoryLog[]> => {
  if (!identifier) {
    return [];
  }

  const collectionPaths = [
    FirestoreCollectionReference.callingHistoryLogs(identifier),
    ...Object.values(NON_EPA_CHANNEL_DOC_IDS).map((channelDocId) =>
      FirestoreCollectionReference.nonEpaHistoryLogs(identifier, channelDocId),
    ),
  ];

  const logsPerPath = await Promise.all(collectionPaths.map(fetchLogsAtPath));

  return logsPerPath
    .flat()
    .sort((a, b) => toMillis(b.created_at) - toMillis(a.created_at));
};

/**
 * Permanently delete one history log document straight from Firestore.
 *
 * Deletes through the Firestore SDK (`deleteDoc`, wrapped by
 * {@link FirestoreService.deleteDocument}) rather than a backend endpoint, so
 * the caller's Firestore rules govern the write.
 *
 * @input log - History entry to remove; needs the `id` and `collection_path`
 *              stamped on by {@link fetchCallingHistoryLogs}
 * @return Resolves once the document is deleted
 * @throws Error if the entry is missing its id or source collection path, or
 *         if the Firestore delete fails
 */
export const deleteCallingHistoryLog = async (
  log: CallingHistoryLog,
): Promise<void> => {
  if (!log.id || !log.collection_path) {
    throw new Error(
      "History entry is missing the document id or collection path required to delete it",
    );
  }

  await FirestoreService.deleteDocument(log.collection_path, log.id);
};
